/**
 * DOZY FIELD — casca da aplicação do agente de terreno.
 *
 * Não é o painel de admin com menos itens: é outra aplicação. O agente está
 * de pé, na rua, com uma mão no telemóvel e a outra a segurar um BI. Por isso
 * não há barra lateral, os alvos são grandes, e o que interessa está sempre
 * a um toque.
 */
import React, { useContext } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Home, Users, LogOut, WifiOff, RefreshCw, AlertTriangle } from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import { useOutbox } from '../../hooks/useOutbox';
import FieldHome from './FieldHome';
import FieldNewWorker from './FieldNewWorker';
import FieldWorkers from './FieldWorkers';
import FieldInterview from './FieldInterview';
import FieldTraining from './FieldTraining';

const NAV = [
  { para: '/admin', rotulo: 'Início', icone: Home, exacto: true },
  { para: '/admin/workers', rotulo: 'Os meus', icone: Users },
];

const FieldLayout = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const local = useLocation();
  const { pendentes, comErro, aSincronizar, online, sincronizar } = useOutbox();

  const sair = async () => { await logout(); navigate('/'); };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <header className="flex items-center gap-3 px-4 py-3 border-b border-slate-800 sticky top-0 bg-slate-950 z-20">
        <span className="grid place-items-center w-9 h-9 rounded-xl bg-blue-600 font-bold">D</span>
        <div className="flex-1 min-w-0">
          <div className="font-bold leading-tight">DOZY FIELD</div>
          <div className="text-xs text-slate-500 truncate">{user?.full_name}</div>
        </div>
        <button
          onClick={sair}
          aria-label="Terminar sessão"
          className="grid place-items-center w-11 h-11 rounded-xl text-slate-400 hover:text-red-400 hover:bg-slate-900"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </header>

      {/* O agente precisa de saber, a todo o momento, o que já saiu do
          telefone e o que ainda lá está. Esconder isto é como perder o
          trabalho sem avisar. */}
      {!online && (
        <div className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-slate-300 text-sm">
          <WifiOff className="w-4 h-4 shrink-0" />
          Sem ligação — pode continuar a cadastrar. Envia-se sozinho depois.
        </div>
      )}

      {pendentes > 0 && (
        <button
          onClick={() => sincronizar({ manual: true })}
          disabled={aSincronizar || !online}
          className="w-full flex items-center gap-2 px-4 py-2 bg-amber-500/15 text-amber-300 text-sm text-left disabled:opacity-70"
        >
          <RefreshCw className={`w-4 h-4 shrink-0 ${aSincronizar ? 'animate-spin' : ''}`} />
          <span className="flex-1">
            {aSincronizar
              ? 'A enviar…'
              : `${pendentes} cadastro${pendentes > 1 ? 's' : ''} por enviar`}
          </span>
          {online && !aSincronizar && <span className="text-xs underline">Enviar agora</span>}
        </button>
      )}

      {comErro > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 bg-red-500/15 text-red-300 text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {comErro} cadastro{comErro > 1 ? 's' : ''} recusado{comErro > 1 ? 's' : ''} — fale com o Gestor de operações.
        </div>
      )}

      <main className="flex-1 p-4 pb-24">
        <Routes>
          <Route path="/" element={<FieldHome />} />
          <Route path="/workers" element={<FieldWorkers />} />
          <Route path="/workers/new" element={<FieldNewWorker />} />
          <Route path="/workers/:id/interview" element={<FieldInterview />} />
          <Route path="/workers/:id/training" element={<FieldTraining />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>

      <nav className="fixed bottom-0 inset-x-0 flex border-t border-slate-800 bg-slate-950 pb-[env(safe-area-inset-bottom)]">
        {NAV.map(({ para, rotulo, icone: Icone, exacto }) => {
          const activo = exacto ? local.pathname === para : local.pathname.startsWith(para);
          return (
            <button
              key={para}
              onClick={() => navigate(para)}
              aria-current={activo ? 'page' : undefined}
              className={`flex-1 flex flex-col items-center gap-1 py-3 text-xs font-medium
                ${activo ? 'text-blue-400' : 'text-slate-500'}`}
            >
              <Icone className="w-5 h-5" />
              {rotulo}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default FieldLayout;
